import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { usePageSeo } from "@/hooks/usePageSeo";

const CONTACT_URL = "https://readdy.ai/api/form/daglbsptto0ceqqnhuk0";

const values = [
  { icon: "ri-eye-line", title: "只盯新面孔", desc: "不做陈年旧闻盘点，专注记录那些刚冒头、正在被看见的新晋人物与新兴人物。" },
  { icon: "ri-mental-health-line", title: "理性不站队", desc: "只梳理破圈轨迹与热议争议，不预设立场、不下绝对结论，把判断交给读者。" },
  { icon: "ri-scales-3-line", title: "虚构不传谣", desc: "本站人物皆为虚构示例，拒绝以档案内容对任何真实个体做负面定性。" },
  { icon: "ri-refresh-line", title: "每日更新", desc: "工作日双更、周末出周榜，帮你在最快节奏里跟上每一个新瓜主角。" },
];

export default function About() {
  usePageSeo(
    "关于新瓜档案｜新晋人物吃瓜档案的内容准则 - 新瓜档案",
    "了解新瓜档案：一个专注记录新晋人物、新爆料主角与新兴人物的吃瓜档案站。了解我们的品牌定位、内容原则、用户口碑与联系方式。",
    {
      id: "about-jsonld",
      data: {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于新瓜档案",
        description: "新瓜档案专注记录新晋人物、新爆料主角与新兴人物的吃瓜档案站。",
      },
    }
  );

  const [status, setStatus] = useState<{ kind: "idle" } | { kind: "ok" } | { kind: "err"; msg: string }>({ kind: "idle" });
  const [pending, setPending] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    const fd = new FormData(e.currentTarget);
    const hp = String(fd.get("website_alt") || "").trim();
    if (hp) {
      setStatus({ kind: "ok" });
      e.currentTarget.reset();
      return;
    }
    const payload = new URLSearchParams();
    ["name", "email", "subject", "message"].forEach((k) => {
      const v = String(fd.get(k) || "").trim();
      if (v) payload.append(k, v);
    });
    setPending(true);
    try {
      const res = await fetch(CONTACT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const text = await res.text();
      let code = "";
      let msg = "";
      try {
        const parsed = JSON.parse(text);
        code = parsed?.code || "";
        msg = parsed?.meta?.message || parsed?.message || "";
      } catch {
        code = "";
      }
      if (res.ok && code === "OK") {
        setStatus({ kind: "ok" });
        e.currentTarget.reset();
      } else {
        setStatus({ kind: "err", msg: msg || "提交失败，请稍后再试。" });
      }
    } catch {
      setStatus({ kind: "err", msg: "网络异常，请稍后再试。" });
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen bg-background-50 text-foreground-900">
      <Header />

      <main>
        {/* 页头 */}
        <section className="relative overflow-hidden border-b border-background-200 bg-background-950">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20editorial%20desk%20with%20open%20notebook%20and%20magnifying%20glass%20over%20newspaper%20clippings%2C%20pen%20and%20coffee%2C%20dark%20walnut%20table%2C%20soft%20amber%20studio%20light%2C%20cozy%20journalism%20newsroom%20aesthetic%2C%20high%20detail&width=1600&height=360&seq=about-hero-2026-01&orientation=landscape"
            alt="新瓜档案编辑室"
            className="absolute inset-0 h-full w-full object-cover opacity-35"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/60 to-background-950/40" />
          <div className="relative container-x py-16">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-background-200/70">
              <Link to="/" className="hover:text-accent-300">首页</Link>
              <i className="ri-arrow-right-s-line" />
              <span className="text-background-50">关于本站</span>
            </nav>
            <h1 className="mt-5 font-heading text-3xl font-black leading-snug text-background-50 md:text-4xl">一本认真记录「新面孔」的吃瓜档案</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/80">
              新瓜档案是一个内容型栏目站，把镜头对准那些刚冒头的新晋人物、新爆料主角与新兴人物，记录他们如何被看见、被热议，也守护每一位观众理性吃瓜。
            </p>
          </div>
        </section>

        {/* 品牌定位 + 发展理念 */}
        <section className="container-x grid gap-10 py-14 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">品牌定位</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">只做「新面孔瓜」的内容索引</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-[1.9] text-foreground-600">
              <p>
                每当一个此前无人知晓的新面孔突然走红，信息总是碎片而混乱：他是谁、因何破圈、为什么被议论、路人盘怎么看。新瓜档案把这些散落的信息整理成一份可检索的「人物档案」，让观众用最短时间认清最新吃瓜主角。
              </p>
              <p>
                我们刻意把范围收窄到「新晋人物」。不做老瓜翻炒，不追逐陈年旧闻，只盯住那些正处于热度上升期的面孔——这既是栏目的辨识度，也是对信息质量的一种克制。
              </p>
              <p>
                从演艺新星到短剧新秀，从选秀回锅到直播新锐，再到边卖边火的带货新势力，我们把新兴人物放进同一套「档案」框架，方便读者横向对比，也方便按栏目深挖。
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">发展理念</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">流量会退潮，档案留得住</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-[1.9] text-foreground-600">
              <p>
                我们相信「吃瓜」不等于「狂欢」。一个新面孔爆火的第一周最热闹，但真正有价值的信息——他来自哪里、积累了什么、为何能接住这波流量——往往藏在喧嚣之下。
              </p>
              <p>
                因此我们坚持给每份档案补充「热度轨迹」，把起点、转折、破圈点串成一条可读的线。我们看好的不是一时的热度数值，而是一个人物档案能否经得起时间回看。
              </p>
              <p>
                这也是为什么我们坚持虚构示例与理性提示：记录是为了理解现象，而不是为了让任何真实的人被舆论伤害。
              </p>
            </div>
          </div>
        </section>

        {/* 内容原则 */}
        <section className="border-y border-background-200 bg-background-100/60">
          <div className="container-x py-14">
            <div className="max-w-xl">
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">内容原则</p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">我们如何记录这些新面孔</h2>
            </div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg bg-background-50 p-6 ring-1 ring-background-200">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600"><i className={`${v.icon} text-xl`} /></span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 用户口碑 */}
        <section className="container-x py-14">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">读者口碑</p>
            <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">读者这样说</h2>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              { name: "橘子汽水", text: "以前追新面孔全靠刷评论区猜，现在一份档案就讲清楚前因后果，太省心了。" },
              { name: "夜航星", text: "最打动我的是「热度轨迹」，原来每个爆火背后真的都有一段铺垫。" },
              { name: "半糖去冰", text: "理性又不失趣味，不会为了流量乱定性，是我每天睡前必看的栏目。" },
            ].map((t, i) => (
              <figure key={i} className="rounded-lg bg-background-100 p-6 ring-1 ring-background-200">
                <div className="flex gap-1 text-accent-500"><i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" /><i className="ri-star-fill" /></div>
                <blockquote className="mt-4 text-[15px] leading-relaxed text-foreground-700">“{t.text}”</blockquote>
                <figcaption className="mt-4 flex items-center gap-2.5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-200 text-secondary-900"><i className="ri-user-smile-line" /></span>
                  <span className="text-sm font-medium text-foreground-800">{t.name}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* 联系 + 免责 */}
        <section className="border-t border-background-200 bg-background-100/60">
          <div className="container-x grid gap-10 py-14 lg:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-accent-600">联系我们</p>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950">有建议或内容反馈？</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                如果你对某份档案的整理方式有看法，或希望推荐一位你关注的新兴人物，欢迎给我们留言。我们会认真阅读每一封来信。
              </p>
              <div className="mt-6 space-y-3 text-sm text-foreground-600">
                <p className="flex items-center gap-2.5"><i className="ri-mail-line text-primary-500" /> hello@xingua-dangan.cn</p>
                <p className="flex items-center gap-2.5"><i className="ri-time-line text-primary-500" /> 工作日 9:00 - 18:00 回复</p>
              </div>

              <div className="mt-8 rounded-lg bg-secondary-100/70 p-5 text-sm leading-relaxed text-secondary-900">
                <p className="flex items-center gap-2 font-semibold"><i className="ri-shield-check-line" /> 免责声明</p>
                <p className="mt-2">
                  本站为虚构内容演示站点，所有人物、机构与事件均为示例，不指向任何真实个体。内容仅供参考娱乐，请理性看待，勿信谣传谣。
                </p>
              </div>
            </div>

            <form onSubmit={onSubmit} data-readdy-form="新瓜档案-联系我们" className="rounded-lg bg-background-50 p-6 ring-1 ring-background-200">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="c-name" className="mb-1.5 block text-sm font-medium text-foreground-700">你的称呼</label>
                  <input id="c-name" name="name" type="text" placeholder="怎么称呼你" className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                </div>
                <div>
                  <label htmlFor="c-email" className="mb-1.5 block text-sm font-medium text-foreground-700">邮箱 *</label>
                  <input id="c-email" name="email" type="email" required autoComplete="email" placeholder="you@example.com" className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="c-subject" className="mb-1.5 block text-sm font-medium text-foreground-700">主题</label>
                  <input id="c-subject" name="subject" type="text" placeholder="例如：栏目建议 / 反馈" className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="c-message" className="mb-1.5 block text-sm font-medium text-foreground-700">留言内容 *</label>
                  <textarea id="c-message" name="message" required rows={5} maxLength={500} placeholder="想对我们说的话（最多 500 字）" className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200" />
                </div>
                <div aria-hidden="true" className="form-aux-field">
                  <input name="website_alt" type="text" tabIndex={-1} autoComplete="off" readOnly />
                </div>
              </div>

              {status.kind === "ok" && (
                <p className="mt-4 flex items-center gap-1.5 rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                  <i className="ri-checkbox-circle-line" /> 感谢留言！我们会尽快回复你。
                </p>
              )}
              {status.kind === "err" && (
                <p className="mt-4 flex items-center gap-1.5 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line" /> {status.msg}
                </p>
              )}

              <button type="submit" disabled={pending} className="btn mt-6 w-full bg-primary-500 py-3 text-background-50 hover:bg-primary-600 disabled:opacity-60">
                {pending ? "提交中…" : "发送留言"} <i className="ri-send-plane-line" />
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}